import React, { useRef, useState } from 'react'

interface VideoAssetProps {
  src: string
  assetId?: number  // Add asset ID for proxy URL generation
  poster?: string
  alt?: string
  style?: React.CSSProperties
  className?: string
  onClick?: (e: React.MouseEvent) => void
  muted?: boolean
  controls?: boolean
  preload?: 'none' | 'metadata' | 'auto'
  showPlayButton?: boolean
  autoPlayOnHover?: boolean
}

// Utility function to generate proxy URL for Meta videos
const getVideoUrl = (src: string, _assetId?: number): string => {
  // For now, try direct URLs first to avoid proxy issues
  // If CORS fails, we can fall back to proxy
  return src
}

const VideoAsset: React.FC<VideoAssetProps> = ({
  src,
  assetId,
  poster,
  alt = 'Video',
  style,
  className,
  onClick,
  muted = true,
  controls = false,
  preload = 'metadata',
  showPlayButton = true,
  autoPlayOnHover = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)

  const handleMouseEnter = () => {
    setIsHovered(true)
    if (autoPlayOnHover && videoRef.current && !isPlaying) {
      videoRef.current.currentTime = 0
      videoRef.current.play().then(() => {
        setIsPlaying(true)
      }).catch((error) => {
        console.warn('Auto-play failed:', error)
      })
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    if (autoPlayOnHover && videoRef.current && isPlaying) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
      setIsPlaying(false)
    }
  }

  const handleError = () => {
    setHasError(true)
  }

  const handleVideoClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e)
    } else if (controls) {
      // Let native video controls handle the click
      return
    } else {
      // Toggle play/pause
      e.stopPropagation()
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause()
          setIsPlaying(false)
        } else {
          videoRef.current.play().then(() => {
            setIsPlaying(true)
          }).catch((error) => {
            console.warn('Manual play failed:', error)
          })
        }
      }
    }
  }

  if (hasError) {
    return (
      <div 
        className={`video-asset-error ${className || ''}`}
        style={style}
      >
        <div className="video-asset-error-icon">🎥</div>
        <div className="video-asset-error-text">VIDEO</div>
        <div style={{ fontSize: '10px', opacity: 0.7 }}>Failed to load</div>
      </div>
    )
  }

  return (
    <div
      className={`video-asset-container ${isPlaying ? 'playing' : ''} ${className || ''}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: 'pointer',
        ...style
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleVideoClick}
      tabIndex={0}
      role="button"
      aria-label={`Video: ${alt}`}
    >
      <video
        ref={videoRef}
        src={getVideoUrl(src, assetId)}
        poster={poster}
        className="video-asset-video"
        muted={muted}
        controls={controls}
        preload={preload}
        playsInline
        loop={autoPlayOnHover}
        onError={handleError}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 'inherit',
        }}
      />
      
      {/* Play button overlay - only show when not playing and showPlayButton is true */}
      {showPlayButton && !isPlaying && !controls && (
        <div className="video-asset-play-button">
          ▶
        </div>
      )}

      {/* Hover overlay effect */}
      {isHovered && autoPlayOnHover && (
        <div className="video-asset-overlay" />
      )}
    </div>
  )
}

export default VideoAsset 